import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import * as actions from '~actions';
import { useDispatch, useSelector, useSplitPayment, useAdyen, useButton, useI18n } from '~Hooks';
import PaymentStatus from '~Components/PaymentStatus';
import PastPayments from './Components/PastPayments';
import PaymentMethods from './Components/PaymentMethods';
import { useStorage } from '~Components/Storage';
import { BlueButton, Box, Button } from '~Styles';
import Modal, { insideWrapperStyles } from '~ComponentsImproved/Modal';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import CancelIcon from '~/Assets/icons/close.svg';
import useModal from '~ComponentsImproved/Modal/useModal';
import {
  SplitPayCancelButton,
  SplitPayModalWrapper,
} from '~Scenes/Tables/Components/PayWith/SplitPayment';
import Icon from '~ComponentsImproved/Icon';

const ModalWrapper = styled(Box)`
  position: relative;
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  width: 600px;
`;
interface Props {
  orderId: string;
  total: number;
  onClose: () => void;
  appliedDiscount: any;
  standardPay: (appliedDiscount: any, method: string) => void;
  onComplete: () => void;
}

const SplitPayment: React.FunctionComponent<Props> = ({
  orderId,
  total,
  onClose,
  appliedDiscount,
  standardPay,
  onComplete,
}) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const [storage] = useStorage();

  const confirmRefundModal = useModal();

  const POSOrders = useSelector((state) => state.POSOrders);
  const nextRefundRequest = useSelector((state) =>
    state.refundBridgeRequests.find(
      (req) =>
        req.orderId === orderId &&
        req.reference === splitPayment.nextRefundPayment?.meta?.paymentId,
    ),
  );

  const [isRefunding, setIsRefunding] = useState<boolean>(false);
  const [isSplitting, setIsSplitting] = useState<boolean>(false);

  const splitPayment = useSplitPayment(orderId);
  const { refundPayment: adyenRefund } = useAdyen();

  /**
   * Mark payment as refunded is refund request is a success
   */
  useEffect(() => {
    if (splitPayment.nextRefundPayment && nextRefundRequest?.status === 'SUCCESS') {
      dispatch({
        type: actions.REFUND_SPLIT_PAYMENT,
        payment: splitPayment?.nextRefundPayment?.id,
      });
    }
  }, [splitPayment.nextRefundPayment, nextRefundRequest]);

  /**
   * Reset POSOrder when cleared
   */
  useEffect(() => {
    if (!splitPayment.splitOrder && !!POSOrders.find((order) => order.id === orderId)) {
      setIsRefunding(false);
      setIsSplitting(false);
      dispatch({
        type: actions.CLEAR_ORDER,
        id: orderId,
      });
      dispatch({
        type: actions.REMOVE_POS_ORDER,
        id: orderId,
      });
    }
  }, [splitPayment.splitOrder]);

  const amountRemaining = total - splitPayment.amountPaid;

  const disabled = Boolean(
    splitPayment.splitOrder &&
      ((!!splitPayment.latestPOSPayment && splitPayment.latestPOSPayment.status !== 'COMPLETED') ||
        isRefunding ||
        amountRemaining <= 0),
  );

  const cancelButton = useButton({
    onClick: () => {
      if (splitPayment.splitOrder || isSplitting) {
        confirmRefundModal.show();
      } else {
        close();
      }
    },
    disabled,
  });

  const cancelConfirmButton = useButton({
    onClick: () => {
      confirmRefundModal.hide();
    },
  });

  const confirmRefundButton = useButton({
    onClick: () => {
      if (splitPayment.splitOrder) {
        setIsRefunding(true);
      } else if (isSplitting) {
        setIsSplitting(false);
      }

      confirmRefundModal.hide();
    },
  });

  const refundPayment = () => {
    if (!isRefunding) return;
    if (!splitPayment.nextRefundPayment) return;

    const {
      id,
      method,
      amount,
      meta: { paymentId, transactionId } = {},
    } = splitPayment.nextRefundPayment;

    if (method === 'IZETTLE') {
      dispatch({
        type: actions.REFUND_BRIDGE_REQUEST,
        orderId,
        reference: paymentId!,
        amount,
      });

      window.postMessageBridge({
        refund: { id, reference: paymentId!, amount },
      });

      return;
    }

    if (method === 'ADYEN') {
      dispatch({
        type: actions.REFUND_BRIDGE_REQUEST,
        orderId,
        reference: paymentId!,
        amount,
      });

      adyenRefund({
        transactionId: transactionId!,
        terminalId: storage.adyenTerminals[0]?.POIID,
      }).then(({ success, message = '' }) => {
        if (success) {
          dispatch({
            type: 'MESSAGE_REFUND_BRIDGE_REQUEST_SUCCESS',
            reference: transactionId!,
          });
        } else {
          dispatch({
            type: 'MESSAGE_REFUND_BRIDGE_REQUEST_FAILED',
            reference: transactionId!,
            error: message,
          });
        }
      });

      return;
    }

    // Fallback: other payment methods
    dispatch({
      type: actions.REFUND_SPLIT_PAYMENT,
      payment: id,
    });
  };

  const close = () => {
    if (isRefunding) {
      return;
    }
    onClose();
  };

  const refundStatus =
    nextRefundRequest?.status === 'SUCCESS'
      ? 'COMPLETED'
      : nextRefundRequest?.error || nextRefundRequest?.status === 'FAILED'
      ? 'FAILED'
      : 'PENDING';

  return (
    <>
      <SplitPayModalWrapper display="flex" flexDirection="column" gap="large" padding="2xl">
        <SplitPayCancelButton display="flex" alignItems="center" {...cancelButton}>
          <Icon src={CancelIcon} height="32px" width="32px" color="var(--colorBlueGray800)" />
          {i18n('General.Cancel')}
        </SplitPayCancelButton>

        <Heading level={1} color="var(--colorWhite)">
          {i18n('SplitPayment.Title')}
        </Heading>

        <PastPayments
          isRefunding={isRefunding}
          orderId={orderId}
          total={total}
          amountRemaining={amountRemaining}
        />

        {splitPayment.latestPOSPayment?.status === 'PENDING' ? (
          <PaymentStatus
            status={isRefunding ? refundStatus : splitPayment.latestPOSPayment.status}
            paymentId={splitPayment.latestPOSPayment.id}
            retryRefund={refundPayment}
            isRefunding={isRefunding}
            isSplitting
          />
        ) : (
          <PaymentMethods
            orderId={orderId}
            total={total}
            appliedDiscount={appliedDiscount}
            onComplete={onComplete}
            disabled={disabled}
            isRefunding={isRefunding}
            setIsRefunding={setIsRefunding}
            refundPayment={refundPayment}
            onClose={close}
            standardPay={standardPay}
          />
        )}
      </SplitPayModalWrapper>

      <Modal isShowing={confirmRefundModal.isShowing} hide={confirmRefundModal.hide}>
        <ModalWrapper
          display="flex"
          flexDirection="column"
          gap="medium"
          alignItems="center"
          justifyContent="center"
          padding="large">
          <Box display="flex" flexDirection="column" gap="small" style={{ textAlign: 'center' }}>
            <Heading level={1} color="var(--colorWhite)">
              {i18n('SplitPayment.Refund.Title')}
            </Heading>
            <Paragraph level={3} color="var(--colorBlueGray200)">
              {i18n('SplitPayment.Refund.Desc')}
            </Paragraph>
          </Box>

          <Box fluid display="flex" gap="large" marginTop="medium">
            <Button fluid {...cancelConfirmButton}>
              {i18n('General.Cancel')}
            </Button>
            <BlueButton fluid {...confirmRefundButton}>
              {i18n('History.Refund')}
            </BlueButton>
          </Box>
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default SplitPayment;
